import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getSystemStatus } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import useWhaleSummary from "../hooks/useWhaleSummary";

const OVERALL_STATUS_STYLES = {
    ok: "bg-emerald-500/20 text-emerald-100 border border-emerald-400/60",
    degraded: "bg-amber-500/20 text-amber-100 border border-amber-300/60",
    down: "bg-rose-500/20 text-rose-100 border border-rose-300/60",
};

const MODULE_STATUS_STYLES = {
    ok: "text-emerald-300",
    stale: "text-amber-300",
    degraded: "text-amber-300",
    warning: "text-amber-300",
    missing: "text-amber-200",
    inactive: "text-gray-400",
    error: "text-rose-300",
    malformed: "text-rose-300",
    unknown: "text-gray-300",
    fresh: "text-emerald-300",
};

const MODULE_STATUS_LABELS = {
    ok: "OK",
    stale: "Stale",
    degraded: "Degraded",
    warning: "Warning",
    missing: "Missing",
    inactive: "Inactive",
    error: "Error",
    malformed: "Malformed",
    unknown: "Unknown",
    fresh: "Fresh",
};

function formatAge(ageSeconds) {
    if (ageSeconds === null || ageSeconds === undefined) return "Unknown";
    if (ageSeconds < 1) return "just now";
    if (ageSeconds < 90) return `${Math.round(ageSeconds)}s ago`;
    if (ageSeconds < 3600) return `${Math.round(ageSeconds / 60)}m ago`;
    const hours = Math.round(ageSeconds / 3600);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
}

function formatTimestamp(ts) {
    if (!ts) return "Unknown";
    try {
        const date = new Date(ts);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    } catch {
        return String(ts);
    }
}

function ModuleStatusDot({ status }) {
    const color = MODULE_STATUS_STYLES[status] || "text-gray-300";
    return <span className={`${color} inline-block w-2 h-2 rounded-full bg-current`} aria-hidden />;
}

ModuleStatusDot.propTypes = {
    status: PropTypes.string,
};

const MODULE_CANONICAL_KEYS = {
    signal_engine: "dream_engine",
    signalengine: "dream_engine",
    dream_engine: "dream_engine",
    dreamengine: "dream_engine",
    dream: "dream_engine",
    "signal engine": "dream_engine",
    nebula_horizon: "horizon_engine",
    horizon_engine: "horizon_engine",
    horizon: "horizon_engine",
    reporter: "nebula_relayer",
    nebula_reporter: "nebula_relayer",
    "nebula-reporter": "nebula_relayer",
    nebula_relayer: "nebula_relayer",
    "nebula-relayer": "nebula_relayer",
    relay: "nebula_relayer",
    relayer: "nebula_relayer",
};

const MODULE_NAME_MAP = {
    nebula_core: "Nebula Core",
    nebula_api: "Nebula API",
    nebula_horizon: "Horizon Engine",
    horizon_engine: "Horizon Engine",
    scalping_engine: "Scalping Engine",
    signal_engine: "Dream Engine",
    dream_engine: "Dream Engine",
    "Signal Engine": "Dream Engine",
    "signal engine": "Dream Engine",
    nebula_relayer: "Relayer",
    relayer: "Relayer",
    reporter: "Relayer",
    nebula_reporter: "Relayer",
    signal_scoring: "Signal Scoring",
    nebula_dashboard: "Dashboard",
    dashboard: "Dashboard",
    heartbeat: "Heartbeat",
};

const STATUS_SEVERITY = { inactive: -1, ok: 0, fresh: 0, stale: 1, degraded: 2, warning: 2, unknown: 2, missing: 2, malformed: 3, error: 3 };

function pickPreferredModule(existing, candidate) {
    if (!existing) return candidate;
    const existingSeverity = STATUS_SEVERITY[existing.status] ?? -1;
    const candidateSeverity = STATUS_SEVERITY[candidate.status] ?? -1;
    const existingTs = existing.last_heartbeat_ts || existing.last_heartbeat;
    const candidateTs = candidate.last_heartbeat_ts || candidate.last_heartbeat;
    const strictUtcZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
    const parseValidUtcZ = (value) => {
        if (!value || !strictUtcZ.test(String(value))) return null;
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : null;
    };
    const existingDate = parseValidUtcZ(existingTs);
    const candidateDate = parseValidUtcZ(candidateTs);

    if (candidateDate != null && existingDate != null && candidateDate !== existingDate) {
        return candidateDate > existingDate ? candidate : existing;
    }
    if (candidateDate != null && existingDate == null) return candidate;
    if (candidateDate == null && existingDate != null) return existing;
    if (candidateSeverity > existingSeverity) return candidate;
    if (candidateSeverity < existingSeverity) return existing;
    if (candidate.is_backend_canonical && !existing.is_backend_canonical) return candidate;
    if (!candidate.is_backend_canonical && existing.is_backend_canonical) return existing;
    if (candidate.age_seconds != null && existing.age_seconds != null) {
        return candidate.age_seconds < existing.age_seconds ? candidate : existing;
    }
    return existing;
}

function normalizeModules(modules = []) {
    const rawList = (() => {
        if (!Array.isArray(modules) && modules && typeof modules === "object") {
            return Object.entries(modules).map(([name, details]) => ({ ...details, name }));
        }
        return Array.isArray(modules) ? modules : [];
    })();

    const merged = new Map();

    const normalizeKey = (rawName) =>
        (rawName || "")
            .toString()
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "")
            .replace(/__+/g, "_");

    rawList.forEach((mod) => {
        const rawName = mod.name || mod.id || mod.label || "module";
        const normalizedKey = normalizeKey(rawName);
        const canonical =
            MODULE_CANONICAL_KEYS[normalizedKey] || MODULE_CANONICAL_KEYS[rawName] || MODULE_CANONICAL_KEYS[rawName?.toLowerCase?.()] || rawName;
        const canonicalBackendName = normalizedKey === normalizeKey(canonical);
        const display = MODULE_NAME_MAP[canonical] || mod.display_name || mod.label || canonical;
        const rawLast = mod.last_heartbeat_ts || mod.last_heartbeat || mod.latest_heartbeat_timestamp || mod.timestamp || null;
        const strictUtcZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
        const last = rawLast && strictUtcZ.test(String(rawLast)) ? rawLast : null;
        const age = last ? (mod.age_seconds ?? mod.age ?? null) : null;
        const normalized = {
            ...mod,
            name: canonical,
            display_name: display,
            last_heartbeat_ts: last,
            age_seconds: age,
            status: mod.status || mod.freshness || mod.health || (last ? "unknown" : "missing"),
            is_backend_canonical: Boolean(canonicalBackendName || mod.is_backend_canonical),
        };
        const existing = merged.get(canonical);
        merged.set(canonical, pickPreferredModule(existing, normalized));
    });

    return Array.from(merged.values());
}

export default function SystemStatusPanel({ className = "" }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { markUnauthorized, authenticated: isAuthenticated } = useAuth();
    const whaleSummary = useWhaleSummary({
        authenticated: isAuthenticated !== false,
        pollIntervalMs: 30000,
        onUnauthorized: markUnauthorized,
    });

    const loadStatus = useCallback(async (withSpinner = false) => {
        if (withSpinner) setLoading(true);
        try {
            const res = await getSystemStatus();
            if (res.unauth) {
                setError("Please log in to view system status.");
                markUnauthorized();
                setStatus(null);
                return;
            }
            if (!res.ok || !res.data) {
                setError("Unable to load system status. Retrying…");
                if (res.data) {
                    setStatus({ ...res.data, modules: normalizeModules(res.data?.modules) });
                } else {
                    setStatus((prev) => prev ?? null);
                }
                return;
            }
            setStatus({ ...res.data, modules: normalizeModules(res.data?.modules) });
            setError("");
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("System status load failed", err);
            setError("Unable to load system status. Retrying…");
            setStatus((prev) => prev ?? null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus(true);
        const interval = setInterval(() => loadStatus(false), 25000);
        return () => clearInterval(interval);
    }, [loadStatus]);

    const mode = status?.mode || {
        trading_mode: status?.trading_mode || "paper",
        safe_mode: status?.safe_mode || false,
        shutdown_blocked: status?.shutdown_requested || false,
    };
    const modules = useMemo(
        () => (Array.isArray(status?.modules) ? [...status.modules] : []),
        [status?.modules],
    );
    const metrics = status?.system_metrics || status?.metrics || null;
    const overallStatus = status?.overall_status || "degraded";
    const overallStyle = OVERALL_STATUS_STYLES[overallStatus] || OVERALL_STATUS_STYLES.degraded;
    const lastUpdated = status?.last_updated || status?.timestamp;

    const whaleAgeSeconds = useMemo(() => {
        if (!whaleSummary.lastUpdated) return null;
        const ts = new Date(whaleSummary.lastUpdated);
        if (Number.isNaN(ts.getTime())) return null;
        return Math.max(0, (Date.now() - ts.getTime()) / 1000);
    }, [whaleSummary.lastUpdated]);

    const whaleRow = useMemo(() => {
        const alerts = whaleSummary.recentAlertCount ?? 0;
        const biasLabel = whaleSummary.maxBiasLevel ? whaleSummary.maxBiasLevel.toUpperCase() : "NONE";
        const biasNote = biasLabel === "NONE" ? "Bias: NONE" : `Bias: ${biasLabel}+`;

        if (whaleSummary.error === "unauthorized") {
            return {
                label: "Unavailable",
                status: "missing",
                lastUpdated: whaleSummary.lastUpdated,
                age: whaleAgeSeconds,
                note: "Sign in required",
            };
        }
        if (whaleSummary.error) {
            return {
                label: "Unavailable",
                status: "missing",
                lastUpdated: whaleSummary.lastUpdated,
                age: whaleAgeSeconds,
                note: "Whale data unavailable",
            };
        }
        if (whaleSummary.loading) {
            return {
                label: "Loading",
                status: "stale",
                lastUpdated: whaleSummary.lastUpdated,
                age: whaleAgeSeconds,
                note: "Syncing whale signals",
            };
        }
        if (!whaleSummary.biases?.length || whaleSummary.influenceEnabled === false) {
            return {
                label: "Calm",
                status: "ok",
                lastUpdated: whaleSummary.lastUpdated,
                age: whaleAgeSeconds,
                note: `${biasNote} · Alerts(1h): ${alerts}`,
            };
        }

        const isHot = (whaleSummary.maxBiasLevel || "").toLowerCase() === "high";
        const label = isHot ? "Hot" : "Elevated";
        const status = isHot ? "error" : "stale";
        return {
            label,
            status,
            lastUpdated: whaleSummary.lastUpdated,
            age: whaleAgeSeconds,
            note: `${biasNote} · Alerts(1h): ${alerts}`,
        };
    }, [whaleAgeSeconds, whaleSummary]);

    const sortedRows = useMemo(() => {
        const moduleRows = modules.map((mod) => ({
            type: "module",
            key: mod.name || mod.id || mod.display_name || mod.label,
            label: mod.display_name || mod.label || mod.name || mod.id,
            data: mod,
        }));

        const whaleSignalsRow = {
            type: "whale",
            key: "whale_signals",
            label: "Whale Signals",
            data: whaleRow,
        };

        return [...moduleRows, whaleSignalsRow].sort((a, b) =>
            (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" }),
        );
    }, [modules, whaleRow]);

    return (
        <div
            className={`border border-gray-800 rounded-2xl bg-gray-900/70 p-4 space-y-4 ${className}`}
            data-testid="system-status-panel"
        >
            <div className="flex flex-wrap items-start justify-between gap-4" data-testid="system-status-overall">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`text-[0.75rem] px-3 py-1 rounded-full font-semibold uppercase tracking-wide ${overallStyle}`}
                        >
                            {overallStatus === "ok"
                                ? "OK"
                                : overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-200 border border-gray-700">
                            Mode: {(status?.trading_mode || mode.trading_mode || "paper").toString().toUpperCase()}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-200 border border-gray-700">
                            Safe Mode: {status?.safe_mode ?? mode.safe_mode ? "ON" : "OFF"}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-200 border border-gray-700">
                            Shutdown: {status?.shutdown_requested ?? mode.shutdown_blocked ? "REQUESTED" : "None"}
                        </span>
                    </div>
                    <span className="text-[0.7rem] text-gray-400">Last updated: {formatTimestamp(lastUpdated)}</span>
                </div>
                {metrics && (metrics.cpu_percent !== undefined || metrics.memory_percent !== undefined) && (
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-200" data-testid="system-status-metrics">
                        {metrics.cpu_percent !== undefined && (
                            <span className="flex items-center gap-1">
                                <span className="text-gray-500">CPU:</span>
                                <span className="font-semibold">{metrics.cpu_percent?.toFixed?.(1) ?? metrics.cpu_percent}%</span>
                            </span>
                        )}
                        {metrics.memory_percent !== undefined && (
                            <span className="flex items-center gap-1">
                                <span className="text-gray-500">Memory:</span>
                                <span className="font-semibold">
                                    {metrics.memory_percent?.toFixed?.(1) ?? metrics.memory_percent}%
                                </span>
                            </span>
                        )}
                        {metrics.disk_usage_percent !== undefined && (
                            <span className="flex items-center gap-1">
                                <span className="text-gray-500">Disk:</span>
                                <span className="font-semibold">
                                    {metrics.disk_usage_percent?.toFixed?.(1) ?? metrics.disk_usage_percent}%
                                </span>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {loading && (
                <div className="text-sm text-gray-400" data-testid="system-status-loading">
                    Loading system status…
                </div>
            )}
            {error && !loading && (
                <div
                    className="text-sm text-amber-200 bg-amber-500/10 border border-amber-400/40 rounded-lg px-3 py-2"
                    data-testid="system-status-error"
                >
                    {error}
                </div>
            )}

            {!loading && !error && (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left" data-testid="system-status-table">
                        <thead className="text-gray-400 uppercase tracking-wide">
                            <tr>
                                <th className="py-2 pr-2">Module</th>
                                <th className="py-2 pr-2">Status</th>
                                <th className="py-2 pr-2">Last Heartbeat</th>
                                <th className="py-2 pr-2">Age</th>
                                <th className="py-2 pr-2">Notes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/80">
                            {sortedRows.map((row) => {
                                if (row.type === "whale") {
                                    return (
                                        <tr key={row.key} data-testid="system-status-whale-row">
                                            <td className="py-2 pr-2 font-semibold text-white">{row.label}</td>
                                            <td
                                                className={`py-2 pr-2 font-semibold flex items-center gap-2 ${MODULE_STATUS_STYLES[row.data.status] || "text-gray-300"}`}
                                            >
                                                <ModuleStatusDot status={row.data.status} />
                                                <span>{row.data.label}</span>
                                            </td>
                                            <td className="py-2 pr-2 text-gray-300">{formatTimestamp(row.data.lastUpdated)}</td>
                                            <td className="py-2 pr-2 text-gray-300">{formatAge(row.data.age)}</td>
                                            <td className="py-2 pr-2 text-gray-400">{row.data.note}</td>
                                        </tr>
                                    );
                                }

                                const mod = row.data;
                                const label = row.label;
                                const statusLabel = MODULE_STATUS_LABELS[mod.status] || mod.status || "Unknown";
                                const statusColor = MODULE_STATUS_STYLES[mod.status] || "text-gray-300";
                                return (
                                    <tr key={row.key}>
                                        <td className="py-2 pr-2 font-semibold text-white">{label}</td>
                                        <td className={`py-2 pr-2 font-semibold flex items-center gap-2 ${statusColor}`}>
                                            <ModuleStatusDot status={mod.status} />
                                            <span>{statusLabel}</span>
                                        </td>
                                        <td className="py-2 pr-2 text-gray-300">{formatTimestamp(mod.last_heartbeat_ts || mod.last_heartbeat)}</td>
                                        <td className="py-2 pr-2 text-gray-300">{formatAge(mod.age_seconds)}</td>
                                        <td className="py-2 pr-2 text-gray-400">{mod.reason || mod.details || ""}</td>
                                    </tr>
                                );
                            })}
                            {modules.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-3 text-gray-400">
                                        No module heartbeats yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

SystemStatusPanel.propTypes = {
    className: PropTypes.string,
};
